import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import MapIcon from '@mui/icons-material/Map';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

// Custom hook to handle login state based on sessionStorage
function useLoginStatus() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Initialize state from sessionStorage or default to false
    return sessionStorage.getItem('isLoggedIn') === 'true';
  });

  useEffect(() => {
    // Watch for changes in sessionStorage
    const onStorageChange = () => {
      setIsLoggedIn(sessionStorage.getItem('isLoggedIn') === 'true');
    };

    // Listen for storage events (e.g., when sessionStorage is changed in another tab)
    window.addEventListener('storage', onStorageChange);

    return () => {
      // Clean up the listener when the component is unmounted
      window.removeEventListener('storage', onStorageChange);
    };
  }, []);

  return [isLoggedIn, setIsLoggedIn];
}

function CustomNavbar({ theme, onToggleTheme }) {
  const [isLoggedIn, setIsLoggedIn] = useLoginStatus();

  const handleLogout = () => {
    // Remove login status from sessionStorage and update the state
    sessionStorage.removeItem('isLoggedIn');
    setIsLoggedIn(false);
    window.location.href = '/login';
  };

  const themeLabel = theme === 'light' ? 'Dark' : 'Light';

  return (
    <Navbar expand="lg" sticky="top" className="admin-navbar">
      <Container fluid="lg">
        <Navbar.Brand as={Link} to="/" className="admin-brand">
          <span className="brand-mark">
            <FlightTakeoffIcon fontSize="small" />
          </span>
          <span className="brand-text">
            <span className="brand-title">Genzeylon Travels</span>
            <span className="brand-subtitle">Admin cockpit</span>
          </span>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="navbar-nav" />

        <Navbar.Collapse id="navbar-nav">
          <Nav className="mx-auto">
            <Nav.Link as={NavLink} to="/" end>
              Dashboard
            </Nav.Link>

            <NavDropdown title={<><MapIcon fontSize="small" /> Tours</>} id="tour-dropdown">
              <NavDropdown.Item as={Link} to="/Tours">
                Create a new tour
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/ManageTours">
                Manage tours
              </NavDropdown.Item>
            </NavDropdown>

            <NavDropdown title={<><DirectionsCarIcon fontSize="small" /> Cars</>} id="car-dropdown">
              <NavDropdown.Item as={Link} to="/ManageCars">
                Manage cars
              </NavDropdown.Item>
              <NavDropdown.Item as={Link} to="/AddCars">
                Add rent a car
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>

          <div className="nav-actions">
            <button type="button" className="theme-toggle" onClick={onToggleTheme}>
              {theme === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
              {themeLabel}
            </button>

            {isLoggedIn ? (
              <button type="button" className="admin-logout" onClick={handleLogout}>
                <LogoutIcon fontSize="small" />
                Logout
              </button>
            ) : (
              <Link to="/login" className="admin-login-link">
                <LoginIcon fontSize="small" />
                Login
              </Link>
            )}
          </div>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default CustomNavbar;
